import { InboxOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Drawer,
  Form,
  message,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd/es/upload/interface';
import LayoutWithMenu from 'components/Layout/LayoutWithMenu';
import dayjs from 'dayjs';
import { useScreen } from 'hooks/useScreen';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { queryErrorMessage } from '../CashTransactions/types';
import {
  type CompetitionOptions,
  competitionLabel,
  downloadCsv,
  IMPORT_ACTION_TYPE_LABELS,
  IMPORT_STATUS_TAGS,
  type ImportActionType,
  type ImportRow,
} from './types';

const { Title } = Typography;
const { Dragger } = Upload;

const SCREEN_KEY = 'competition_participant_import';

const TEMPLATE_HEADERS = [
  'submission_key',
  'submitted_at',
  'last_name',
  'first_name',
  'competition_class_year',
  'school_name',
  'participant_class_year',
  'mobile_no',
  'email_address',
  'participation_no',
  'payment_proof_url',
  'exam_room',
  'exam_number',
];
const TEMPLATE_ROW = [
  'a1b2c3',
  '2026/10/05 15:45:12',
  'Бат',
  'Болд',
  '8',
  '23-р сургууль',
  '8б',
  '99112233',
  'bold@example.com',
  '1',
  'https://drive.google.com/open?id=...',
  '201',
  '1',
];

// "Оролцогч импортлох" (CONTEXT.md: Participant import): the list of imports;
// "Шинэ импорт" opens a drawer to pick the Competition and the form's CSV,
// then the comparison step decides what gets written.
export default function ParticipantImportList() {
  const navigate = useNavigate();
  const { appKey } = useParams<{ appKey: string }>();
  const { level, runQuery } = useScreen();
  const canEdit = level === 'edit';

  const [options, setOptions] = useState<CompetitionOptions>({ competitions: [], rooms: [] });
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [competitionId, setCompetitionId] = useState<number | null>(null);
  const [file, setFile] = useState<UploadFile | null>(null);
  const [actionType, setActionType] = useState<ImportActionType>('CREATE_UPDATE');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    runQuery<CompetitionOptions>('competition_options')
      .then(([row]) => row && setOptions(row))
      .catch((e) => message.error(queryErrorMessage(e)));
  }, [runQuery]);

  const load = useCallback(() => {
    setLoading(true);
    runQuery<{ results: ImportRow[]; total: number }>('competition_imports', { page, pageSize })
      .then(([row]) => {
        setRows(row?.results ?? []);
        setTotal(row?.total ?? 0);
      })
      .catch((e) => message.error(queryErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [runQuery, page, pageSize]);

  useEffect(load, [load]);

  const openCompare = (id: number) => navigate(`/apps/${appKey}/${SCREEN_KEY}/compare/${id}`);

  const resetDrawer = () => {
    setDrawerOpen(false);
    setFile(null);
    setActionType('CREATE_UPDATE');
  };

  const handleImport = async () => {
    if (!competitionId) {
      message.warning('Тэмцээн сонгоно уу.');
      return;
    }
    if (!file) {
      message.warning('CSV файл сонгоно уу.');
      return;
    }
    setSubmitting(true);
    try {
      // Decoded as UTF-8; the server rejects a file that wasn't.
      const content = await (file as unknown as File).text();
      const [row] = await runQuery<{ id: number }>('competition_import_create', {
        competitionId,
        actionType,
        fileName: file.name,
        content,
      });
      resetDrawer();
      if (row) openCompare(row.id);
    } catch (e) {
      message.error(queryErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const columns: ColumnsType<ImportRow> = [
    {
      title: 'Огноо',
      dataIndex: 'created_at',
      width: 160,
      render: (value: string) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '—'),
    },
    { title: 'Тэмцээн', dataIndex: 'competition_name', render: (v: string | null) => v ?? '—' },
    { title: 'Файл', dataIndex: 'file_name' },
    {
      title: 'Үйлдлийн төрөл',
      dataIndex: 'action_type',
      render: (value: ImportActionType) => IMPORT_ACTION_TYPE_LABELS[value] ?? value,
    },
    {
      title: 'Төлөв',
      dataIndex: 'status',
      render: (value: ImportRow['status']) => (
        <Tag color={IMPORT_STATUS_TAGS[value]?.color}>
          {IMPORT_STATUS_TAGS[value]?.label ?? value}
        </Tag>
      ),
    },
    { title: 'Нийт', dataIndex: 'total_record', align: 'right' },
    { title: 'Шинээр нэмэгдэх', dataIndex: 'created_record', align: 'right' },
    { title: 'Шинэчлэгдэх', dataIndex: 'updated_record', align: 'right' },
    { title: 'Алдаатай', dataIndex: 'error_record', align: 'right' },
    { title: 'Алгасагдах', dataIndex: 'skipped_record', align: 'right' },
  ];

  return (
    <LayoutWithMenu className="px-3 lg:px-5">
      <Card>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            Оролцогч импортлох
          </Title>
          <Space>
            <Button
              onClick={() => downloadCsv('oroltsogch-import.csv', [TEMPLATE_HEADERS, TEMPLATE_ROW])}
            >
              Жишээ CSV татах
            </Button>
            {canEdit && (
              <Button type="primary" onClick={() => setDrawerOpen(true)}>
                Шинэ импорт
              </Button>
            )}
          </Space>
        </div>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={loading}
          scroll={{ x: true }}
          onRow={(row) => ({ style: { cursor: 'pointer' }, onClick: () => openCompare(row.id) })}
          pagination={{
            current: page + 1,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (p, size) => {
              setPage(size !== pageSize ? 0 : p - 1);
              setPageSize(size);
            },
          }}
        />
      </Card>

      <Drawer
        title="Шинэ импорт"
        open={drawerOpen}
        onClose={resetDrawer}
        placement="right"
        width={640}
        destroyOnHidden
        extra={
          <Space>
            <Button onClick={resetDrawer}>Болих</Button>
            <Button type="primary" onClick={handleImport} loading={submitting}>
              Импортлох
            </Button>
          </Space>
        }
      >
        <Form layout="vertical">
          <Form.Item label="Тэмцээн" required>
            <Select
              placeholder="Тэмцээн сонгох"
              showSearch
              optionFilterProp="label"
              value={competitionId ?? undefined}
              onChange={setCompetitionId}
              options={options.competitions.map((c) => ({
                value: c.id,
                label: competitionLabel(c),
              }))}
            />
          </Form.Item>

          <div
            style={{
              marginBottom: 16,
              padding: '10px 12px',
              borderRadius: 6,
              background: '#f5f5f5',
              color: 'rgba(0, 0, 0, 0.45)',
              fontSize: 13,
            }}
          >
            CSV файлын баганы нэрс (эхний мөр header байх ёстой):
            <br />
            <span style={{ fontFamily: 'monospace' }}>{TEMPLATE_HEADERS.join(', ')}</span>
            <br />
            Заавал: submission_key, first_name, mobile_no, competition_class_year. Файлд байхгүй
            баганын утга өөрчлөгдөхгүй.
          </div>

          <Form.Item label="CSV файл" required>
            <Dragger
              maxCount={1}
              accept=".csv,text/csv"
              height={150}
              fileList={file ? [file] : []}
              beforeUpload={(selected) => {
                setFile(selected);
                return false;
              }}
              onRemove={() => setFile(null)}
            >
              <p className="ant-upload-drag-icon" style={{ marginBottom: 4, fontSize: 24 }}>
                <InboxOutlined />
              </p>
              <p className="ant-upload-text" style={{ fontSize: 13 }}>
                CSV файлаа энд чирж оруулна уу
              </p>
            </Dragger>
          </Form.Item>

          <Form.Item label="Үйлдлийн төрөл">
            <Select<ImportActionType>
              value={actionType}
              onChange={setActionType}
              options={Object.entries(IMPORT_ACTION_TYPE_LABELS).map(([value, label]) => ({
                value: value as ImportActionType,
                label,
              }))}
            />
          </Form.Item>
        </Form>
      </Drawer>
    </LayoutWithMenu>
  );
}
